// Вход и безопасность: /api/auth/*.

import { useQuery } from '@tanstack/react-query'
import type { PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser'

import { api } from './client'

export type AuthState = {
  setup_required: boolean
  authenticated: boolean
  /** Сессия этого устройства заблокирована (см. src/lock). */
  locked?: boolean
  lock_after_s?: number | null
  user?: AuthUser | null
}
export type AuthUser = {
  id: string
  email: string | null
  name: string
  role: 'admin' | 'user'
  totp_enabled: boolean
  requires_email: boolean
  requires_totp_setup: boolean
  legacy_owner: boolean
}
export function isFullAuthentication(state: AuthState | null | undefined): state is AuthState & { user: AuthUser } {
  return Boolean(state?.authenticated && state.user && !state.user.requires_email && !state.user.requires_totp_setup)
}
export type TotpEnrollment = { totp_uri: string; totp_secret_display: string; pending?: string | null }
export type TotpReset = TotpEnrollment & { pending: string }
export type RecoveryCodes = { recovery_codes: string[] }
export type LoginChallenge = { needs_totp: boolean; challenge: string | null }
export type SecurityInfo = { recovery_codes_left: number; totp_enabled: boolean }
type Ok = { ok: true }

export const authKeys = {
  state: ['auth', 'state'] as const,
  security: ['auth', 'security'] as const,
}

export const authApi = {
  state: () => api.get<AuthState>('/auth/state'),
  setup: (email: string, password: string, timezone: string | null) =>
    api.post<TotpEnrollment & { pending: string }>('/auth/setup', { email, password, timezone }),
  confirmSetup: (pending: string, code: string) => api.post<RecoveryCodes>('/auth/setup/confirm', { pending, code }),
  login: (email: string, password: string) => api.post<LoginChallenge>('/auth/login', { ...(email ? { email } : {}), password }),
  loginSecondFactor: (input: {
    challenge: string
    code?: string
    recovery_code?: string
    lock_after_s?: number | null
  }) =>
    api.post<Ok>('/auth/login/totp', input),
  logout: () => api.post<Ok>('/auth/logout'),
  logoutAll: () => api.post<Ok>('/auth/logout-all'),
  startEmailChange: (email: string, password: string) => api.post<Ok>('/auth/email', { email, password }),
  confirmEmail: (token: string) => api.post<Ok>('/auth/confirm-email', { token }),
  invitePreview: (token: string) => api.post<{ email: string; name: string; role: 'admin' | 'user' }>('/auth/invite/preview', { token }),
  acceptInvite: (token: string, password: string) => api.post<Ok>('/auth/invite/accept', { token, password }),
  forgotPassword: (email: string) => api.post<Ok>('/auth/forgot-password', { email }),
  resetPassword: (token: string, password: string) => api.post<Ok>('/auth/reset-password', { token, password }),
  beginTotp: (password: string) => api.post<TotpReset>('/auth/complete/totp', { password }),
  completeTotpConfirm: (pending: string, code: string) => api.post<RecoveryCodes>('/auth/complete/totp/confirm', { pending, code }),
  passkeyOptions: (email: string) => api.post<{ challenge: string; options: PublicKeyCredentialRequestOptionsJSON }>('/auth/login/passkey/options', { email }),
  loginPasskey: (challenge: string, credential: unknown) => api.post<LoginChallenge>('/auth/login/passkey', { challenge, credential }),
  security: () => api.get<SecurityInfo>('/auth/security'),
  changePassword: (current: string, next: string) =>
    api.post<Ok>('/auth/password', { current, new: next }),
  resetTotp: (password: string) => api.post<TotpReset>('/auth/totp/reset', { password }),
  confirmTotp: (pending: string, code: string) =>
    api.post<RecoveryCodes>('/auth/totp/confirm', { pending, code }),
  disableTotp: (input: { password: string; code?: string; recovery_code?: string }) =>
    api.post<Ok>('/auth/totp/disable', input),
  regenerateRecoveryCodes: (password: string) =>
    api.post<RecoveryCodes>('/auth/recovery-codes', { password }),
}

export function useAuthState() {
  return useQuery({ queryKey: authKeys.state, queryFn: authApi.state, staleTime: 60_000 })
}

/** Часовой пояс браузера для первичной настройки. */
export function browserTimezone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null
  } catch {
    return null
  }
}

/** Куда вернуться после входа: только свой путь, не /login и не /setup. */
export function safeNext(next: string | null | undefined): string {
  if (!next || !next.startsWith('/') || next.startsWith('//') || next.startsWith('/\\')) return '/'
  if (next.startsWith('/login') || next.startsWith('/setup')) return '/'
  return next
}

/** Адреса бэкенда (не SPA), на которые после входа нужен полный переход: /oauth/authorize?… */
export function needsFullNavigation(path: string): boolean {
  return path.startsWith('/oauth/') && !path.startsWith('/oauth/consent')
}
